import type { ApiResponse } from '@/types/global'
import axios from '../plugins/axios'

// ===========================
// TypeScript Interfaces
// ===========================

/**
 * Common filter parameters used across all dashboard endpoints
 */
export interface DashboardFilters {
  /** Array of branch IDs to filter by */
  branch?: number[]
  /** Array of mosque IDs to filter by */
  mosque?: number[]
  /** Start date for filtering (ISO format or false) */
  date_from?: string | false
  /** End date for filtering (ISO format or false) */
  date_to?: string | false
  /** Group results by mosque */
  group_by_mosque?: boolean
  /** Group results by branch */
  group_by_branch?: boolean
}

/**
 * Dashboard statistics response structure
 * Uses flexible key-value pairs to accommodate any stat type
 */
export interface DashboardStats {
  [key: string]: number | string | boolean | any
}

// ===========================
// Helpers
// ===========================

/**
 * Normalize dashboard params and auto-enable mosque grouping when a mosque filter is present.
 */
const buildDashboardParams = (filters: DashboardFilters = {}) => {
  const branch = filters.branch || []
  const mosque = filters.mosque || []
  const hasMosqueFilter = Array.isArray(mosque) && mosque.length > 0
  const hasBranchFilter = Array.isArray(branch) && branch.length > 0

  return {
    branch,
    mosque,
    date_from: filters.date_from || false,
    date_to: filters.date_to || false,
    group_by_mosque: filters.group_by_mosque ?? hasMosqueFilter,
    // Disable branch grouping whenever mosque filter is active
    group_by_branch: hasMosqueFilter
      ? false
      : filters.group_by_branch ?? hasBranchFilter
  }
}

// ===========================
// API Functions
// ===========================

/**
 * Fetch all dashboard statistics combined
 *
 * @param filters - Dashboard filter parameters
 * @returns Combined dashboard statistics
 *
 * @example
 * ```typescript
 * const stats = await fetchAllDashboards({
 *   branch: [1, 2],
 *   mosque: [],
 *   date_from: '2024-01-01',
 *   date_to: '2024-12-31',
 *   group_by_mosque: true,
 *   group_by_branch: false
 * })
 * ```
 */
export const fetchAllDashboards = async (
  filters: DashboardFilters = {}
): Promise<DashboardStats> => {
  const response = await axios.post<ApiResponse<DashboardStats>>(
    '/api/1.0/dashboard',
    {
      jsonrpc: '2.0',
      params: buildDashboardParams(filters)
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result
}

/**
 * Fetch volunteer dashboard statistics
 *
 * @param filters - Dashboard filter parameters
 * @returns Volunteer statistics
 *
 * @example
 * ```typescript
 * const volunteerStats = await fetchVolunteerStats({
 *   branch: [1],
 *   date_from: '2024-01-01',
 *   date_to: '2024-12-31'
 * })
 * ```
 */
export const fetchVolunteerStats = async (
  filters: DashboardFilters = {}
): Promise<DashboardStats> => {
  const response = await axios.post<ApiResponse<DashboardStats>>(
    '/api/1.0/dashboard/volunteer',
    {
      jsonrpc: '2.0',
      params: buildDashboardParams(filters)
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result
}

/**
 * Fetch worshippers dashboard statistics
 *
 * @param filters - Dashboard filter parameters
 * @returns Worshippers statistics
 *
 * @example
 * ```typescript
 * const worshippersStats = await fetchWorshippersStats({
 *   mosque: [5, 6],
 *   group_by_mosque: true
 * })
 * ```
 */
export const fetchWorshippersStats = async (
  filters: DashboardFilters = {}
): Promise<DashboardStats> => {
  const response = await axios.post<ApiResponse<DashboardStats>>(
    '/api/1.0/dashboard/worshippers',
    {
      jsonrpc: '2.0',
      params: buildDashboardParams(filters)
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result
}

/**
 * Fetch halaqat (Khatma) dashboard statistics
 *
 * @param filters - Dashboard filter parameters
 * @returns Halaqat statistics
 *
 * @example
 * ```typescript
 * const halaqatStats = await fetchHalaqatStats({
 *   branch: [1, 2, 3],
 *   date_from: '2024-01-01',
 *   group_by_branch: true
 * })
 * ```
 */
export const fetchHalaqatStats = async (
  filters: DashboardFilters = {}
): Promise<DashboardStats> => {
  const response = await axios.post<ApiResponse<DashboardStats>>(
    '/api/1.0/dashboard/halaqat/khatma',
    {
      jsonrpc: '2.0',
      params: buildDashboardParams(filters)
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result
}

/**
 * Fetch deadcare dashboard statistics
 *
 * @param filters - Dashboard filter parameters
 * @returns Deadcare statistics
 *
 * @example
 * ```typescript
 * const deadcareStats = await fetchDeadcareStats({
 *   date_from: '2024-01-01',
 *   date_to: '2024-12-31'
 * })
 * ```
 */
export const fetchDeadcareStats = async (
  filters: DashboardFilters = {}
): Promise<DashboardStats> => {
  const response = await axios.post<ApiResponse<DashboardStats>>(
    '/api/1.0/dashboard/deadcare',
    {
      jsonrpc: '2.0',
      params: buildDashboardParams(filters)
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result
}

/**
 * Fetch Maqari Committed dashboard statistics
 *
 * @param filters - Dashboard filter parameters
 * @returns Maqari statistics
 *
 * @example
 * ```typescript
 * const maqariStats = await fetchMaqariStats({
 *   branch: [1],
 *   mosque: [2, 3],
 *   group_by_mosque: true
 * })
 * ```
 */
export const fetchMaqariStats = async (
  filters: DashboardFilters = {}
): Promise<DashboardStats> => {
  const response = await axios.post<ApiResponse<DashboardStats>>(
    '/api/1.0/dashboard/committed',
    {
      jsonrpc: '2.0',
      params: buildDashboardParams(filters)
    }
  )

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  return response.data.result
}

// ===========================
// Utility Functions
// ===========================

/**
 * Helper function to create default filter parameters
 *
 * @param overrides - Partial filters to override defaults
 * @returns Complete filter object with defaults
 *
 * @example
 * ```typescript
 * const filters = createDefaultFilters({ branch: [1, 2] })
 * // Returns: { branch: [1, 2], mosque: [], date_from: false, date_to: false, group_by_mosque: false, group_by_branch: false }
 * ```
 */
export const createDefaultFilters = (
  overrides: Partial<DashboardFilters> = {}
): DashboardFilters => {
  return {
    branch: [],
    mosque: [],
    date_from: false,
    date_to: false,
    group_by_mosque: false,
    group_by_branch: false,
    ...overrides
  }
}

/**
 * Helper function to validate date range
 *
 * @param date_from - Start date
 * @param date_to - End date
 * @returns True if date range is valid
 */
export const isValidDateRange = (
  date_from: string | false,
  date_to: string | false
): boolean => {
  if (!date_from || !date_to) return true
  return new Date(date_from) <= new Date(date_to)
}
